import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { MessageCircle, MessageSquare, Copy, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const ShareEventModal = ({ event, onClose }) => {
  const [copied, setCopied] = useState(false);

  // Format event date
  const eventDate = new Date(event.date).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Create share message
  const shareMessage = `🎉 You're Invited to ${event.title}${event.invited_by ? `\n\n💝 Invited By: ${event.invited_by}` : ''}

📅 Date: ${eventDate}
📍 Location: ${event.location}

${event.description ? `${event.description}\n\n` : ''}${event.blessings ? `✨ Message: ${event.blessings}\n\n` : ''}Join using Event Code: ${event.event_code}

RSVP & Contribute now at: ${window.location.origin}/event/${event.id}`;

  const shareUrl = `${window.location.origin}/event/${event.id}`;

  // WhatsApp share
  const handleWhatsAppShare = () => {
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareMessage)}`;
    window.open(whatsappUrl, '_blank');
    toast.success('Opening WhatsApp...');
  };

  // SMS share
  const handleSMSShare = () => {
    const smsUrl = `sms:?body=${encodeURIComponent(shareMessage)}`;
    window.location.href = smsUrl;
    toast.success('Opening SMS...');
  };

  // Copy message
  const handleCopyMessage = () => {
    navigator.clipboard.writeText(shareMessage);
    setCopied(true);
    toast.success('Message copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  // Copy link only
  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success('Event link copied!');
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] sm:max-w-lg" data-testid="share-event-modal">
        <DialogHeader>
          <DialogTitle className="text-lg md:text-2xl font-bold">Share Your Event</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 md:space-y-6 py-2 md:py-4">
          {/* Event Preview */}
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-3 md:p-4 rounded-lg border border-purple-200">
            <h3 className="font-semibold text-gray-900 mb-2 text-sm md:text-base">{event.title}</h3>
            <div className="space-y-1 text-xs md:text-sm text-gray-600">
              <p>📅 {eventDate}</p>
              <p>📍 {event.location}</p>
              <p className="font-semibold text-contribo-navy mt-2 text-xs md:text-sm">
                Event Code: <span className="text-lg md:text-2xl tracking-wider">{event.event_code}</span>
              </p>
            </div>
          </div>

          {/* Share Options */}
          <div className="space-y-2 md:space-y-3">
            <p className="text-xs md:text-sm font-medium text-gray-700">Share via:</p>
            
            {/* WhatsApp Button */}
            <Button
              className="w-full bg-green-500 hover:bg-green-600 text-white text-xs md:text-sm py-2"
              onClick={handleWhatsAppShare}
              data-testid="share-whatsapp-btn"
            >
              <MessageCircle className="mr-1 md:mr-2 h-4 w-4 md:h-5 md:w-5" />
              Share on WhatsApp
            </Button>

            {/* SMS Button */}
            <Button
              className="w-full bg-blue-500 hover:bg-blue-600 text-white text-xs md:text-sm py-2"
              onClick={handleSMSShare}
              data-testid="share-sms-btn"
            >
              <MessageSquare className="mr-1 md:mr-2 h-4 w-4 md:h-5 md:w-5" />
              Share via SMS / Text
            </Button>

            {/* Copy Message Button */}
            <Button
              variant="outline"
              className="w-full text-xs md:text-sm py-2"
              onClick={handleCopyMessage}
              data-testid="copy-message-btn"
            >
              {copied ? (
                <>
                  <CheckCircle className="mr-1 md:mr-2 h-4 w-4 md:h-5 md:w-5 text-green-600" />
                  Message Copied!
                </>
              ) : (
                <>
                  <Copy className="mr-1 md:mr-2 h-4 w-4 md:h-5 md:w-5" />
                  Copy Full Message
                </>
              )}
            </Button>

            {/* Copy Link Only Button */}
            <Button
              variant="outline"
              className="w-full"
              onClick={handleCopyLink}
              data-testid="copy-link-btn"
            >
              <Copy className="mr-2 h-5 w-5" />
              Copy Event Link Only
            </Button>
          </div>

          {/* Message Preview */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">Message Preview:</p>
            <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 text-sm whitespace-pre-wrap max-h-48 overflow-y-auto">
              {shareMessage}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareEventModal;
